import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getWorkShiftDetails, updateWorkShift, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { toast } from 'react-toastify';
import { FaArrowRight, FaPlus, FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { applyFieldFilter } from '../../../../shared/utils.js/fieldValidation';

const EditShift = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const { workShiftDetails, isLoading, error, success } = useSelector((state) => state.auth);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    details: [
      { day_of_week: 'saturday', is_off_day: true, start_time: '', end_time: '' },
      { day_of_week: 'sunday', is_off_day: false, start_time: '09:00', end_time: '17:00' },
      { day_of_week: 'monday', is_off_day: false, start_time: '09:00', end_time: '17:00' },
      { day_of_week: 'tuesday', is_off_day: false, start_time: '09:00', end_time: '17:00' },
      { day_of_week: 'wednesday', is_off_day: false, start_time: '09:00', end_time: '17:00' },
      { day_of_week: 'thursday', is_off_day: false, start_time: '09:00', end_time: '17:00' },
      { day_of_week: 'friday', is_off_day: true, start_time: '', end_time: '' }
    ]
  });

  const daysOfWeek = [
    { key: 'saturday', label: t('shifts.add.dayLabels.saturday') },
    { key: 'sunday', label: t('shifts.add.dayLabels.sunday') },
    { key: 'monday', label: t('shifts.add.dayLabels.monday') },
    { key: 'tuesday', label: t('shifts.add.dayLabels.tuesday') },
    { key: 'wednesday', label: t('shifts.add.dayLabels.wednesday') },
    { key: 'thursday', label: t('shifts.add.dayLabels.thursday') },
    { key: 'friday', label: t('shifts.add.dayLabels.friday') }
  ];

  useEffect(() => {
    if (id) {
      dispatch(getWorkShiftDetails(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (workShiftDetails) {
      setFormData({
        name_en: workShiftDetails.name_en || workShiftDetails.name || '',
        name_ar: workShiftDetails.name_ar || workShiftDetails.name || '',
        details: workShiftDetails.details || formData.details
      });
    }
  }, [workShiftDetails]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: isRTL });
        lastSuccessRef.current = { message: success, time: now };
        setTimeout(() => {
          dispatch(clearSuccess());
          navigate('/shifts');
        }, 1500);
      }
    }
  }, [success, dispatch, navigate]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }

      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : applyFieldFilter(name, value);
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleDayChange = (dayIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      details: prev.details.map((day, index) => 
        index === dayIndex 
          ? { ...day, [field]: value }
          : day
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const invalidDays = formData.details.filter(day => 
      !day.is_off_day && (!day.start_time || !day.end_time)
    );

    if (invalidDays.length > 0) {
      toast.error(t('shifts.add.validation.timeRequired'), { rtl: isRTL });
      setIsSubmitting(false);
      return;
    }

    try {
      await dispatch(updateWorkShift({ id, data: formData }));
    } catch (err) {
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #333',
            borderTop: '4px solid #AC2000',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p>{t('shifts.edit.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: 'bold', 
          marginBottom: '20px',
          color: 'white',
          margin: 0,
          textAlign
        }}>
          {t('shifts.edit.title')}
        </h1>
        <button
          onClick={() => navigate('/shifts')}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaArrowRight />
          {t('shifts.edit.back')}
        </button>
      </div>

      <form onSubmit={handleSubmit} style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '30px',
        border: '1px solid #333'
      }}>
        
        <div style={{ marginBottom: '25px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '14px',
            textAlign
          }}>
            {t('shifts.edit.fields.nameAr')}
          </label>
          <input
            type="text"
            name="name_ar"
            value={formData.name_ar}
            onChange={handleInputChange}
            required
            style={{
              width: '100%',
              padding: '12px 15px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              direction: dir,
              textAlign
            }}
            placeholder={t('shifts.edit.placeholders.nameAr')}
          />
        </div>

        <div style={{ marginBottom: '25px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '14px',
            textAlign
          }}>
            {t('shifts.edit.fields.nameEn')}
          </label>
          <input
            type="text"
            name="name_en"
            value={formData.name_en}
            onChange={handleInputChange}
            required
            style={{
              width: '100%',
              padding: '12px 15px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              direction: dir,
              textAlign
            }}
            placeholder={t('shifts.edit.placeholders.nameEn')}
          />
        </div>

        
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{
            color: 'white',
            marginBottom: '20px',
            fontSize: '18px',
            fontWeight: 'bold',
            textAlign
          }}>
            {t('shifts.edit.fields.daysDetails')}
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {formData.details.map((day, index) => (
              <div key={day.day_of_week} style={{
                backgroundColor: '#1a1f2e',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid #333'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '15px'
                }}>
                  <h4 style={{
                    color: 'white',
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    {daysOfWeek.find(d => d.key === day.day_of_week)?.label}
                  </h4>
                  
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: 'white',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={day.is_off_day}
                      onChange={(e) => handleDayChange(index, 'is_off_day', e.target.checked)}
                      style={{
                        width: '18px',
                        height: '18px',
                        accentColor: '#AC2000'
                      }}
                    />
                    {t('shifts.edit.dayFields.offDay')}
                  </label>
                </div>

                {!day.is_off_day && (
                  <div style={{
                    display: 'flex',
                    gap: '15px',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1 }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '5px',
                        color: 'white',
                        fontSize: '14px',
                        textAlign
                      }}>
                        {t('shifts.edit.dayFields.startTime')}
                      </label>
                      <input
                        type="time"
                        value={day.start_time}
                        onChange={(e) => handleDayChange(index, 'start_time', e.target.value)}
                        required={!day.is_off_day}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          backgroundColor: '#14263b',
                          border: '1px solid #333',
                          borderRadius: '6px',
                          color: 'white',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '5px',
                        color: 'white',
                        fontSize: '14px',
                        textAlign
                      }}>
                        {t('shifts.edit.dayFields.endTime')}
                      </label>
                      <input
                        type="time"
                        value={day.end_time}
                        onChange={(e) => handleDayChange(index, 'end_time', e.target.value)}
                        required={!day.is_off_day}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          backgroundColor: '#14263b',
                          border: '1px solid #333',
                          borderRadius: '6px',
                          color: 'white',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        
        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center',
          marginTop: '30px',
          flexDirection: isRTL ? 'row' : 'row-reverse'
        }}>
          <button
            type="button"
            onClick={() => navigate('/shifts')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaArrowRight />
            {t('shifts.edit.buttons.cancel')}
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              backgroundColor: isSubmitting ? '#666' : '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isSubmitting ? 0.7 : 1,
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            {isSubmitting ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #fff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                {t('shifts.edit.buttons.saving')}
              </>
            ) : (
              <>
                <FaPlus />
                {t('shifts.edit.buttons.update')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditShift;
